import type Highcharts from "highcharts";

import { TTemporalDataResponseSerie } from "@/components/ui/chart/types/temporal-chart-series.type";
import { chain } from "@/utils/chain";
import { ICommonSeriesAdapterOptions } from "@/utils/chart/adapters/types/common-adapter.type";
import { ITemporalSeriesAdapter } from "@/utils/chart/adapters/types/temporal-chart-adapter.type";
import { isDefined } from "@/utils/object";
import { EPrefixSymbol, getValueByPrefix } from "@/utils/translation-utils";

export interface ISplineSeriesAdapterOptions extends ICommonSeriesAdapterOptions {
  color: string;
  dashStyle: Highcharts.DashStyleValue;
  valueSuffix: string;
  prefixSymbol: EPrefixSymbol;
  accessibilityDescription: string;
  displayDashedIncompleteZones: boolean;
}

export function SplineSeriesAdapter<KIND extends string>(
  opts: Partial<ISplineSeriesAdapterOptions> = {},
): ITemporalSeriesAdapter<KIND> {
  return {
    adapt(
      data: TTemporalDataResponseSerie<KIND>,
      yAxisOpts?: Array<Highcharts.YAxisOptions>,
    ): Array<Highcharts.SeriesOptionsType> {
      const points = data.value.map(
        (point) =>
          [
            new Date(point.date).getTime(),
            isDefined(point.value) ? getValueByPrefix(point.value, opts.prefixSymbol) : null,
          ] as const,
      );

      const zones: Highcharts.SeriesZonesOptionsObject[] = [];
      if (opts.displayDashedIncompleteZones) {
        chain(data.value)
          .sortBy((_) => new Date(_.date).valueOf())
          .value()
          .forEach((point) => {
            const currentZoneElement: Highcharts.SeriesZonesOptionsObject = {
              value: new Date(point.date).getTime(),
              dashStyle: point.isComplete ? "Solid" : "Dash",
            };

            const lastZoneElement = zones[zones.length - 1];

            if (lastZoneElement && lastZoneElement.dashStyle === currentZoneElement.dashStyle) {
              zones[zones.length - 1] = currentZoneElement;
            } else {
              zones.push(currentZoneElement);
            }
          });
      }

      const results: Array<Highcharts.SeriesOptionsType> = [];
      const unit = data.unit;

      results.push({
        id: data.category,
        name: opts.name ?? data.category,
        data: points,
        type: "spline",
        marker: {
          enabled: false,
        },
        tooltip: {
          valueSuffix: opts.valueSuffix ?? ` ${opts.prefixSymbol ?? ""}${data.unit}`,
        },
        color: opts.color,
        yAxis: yAxisOpts?.findIndex((YAxisOpt) => YAxisOpt.id == unit),
        dashStyle: opts.dashStyle,
        accessibility: {
          description: opts.accessibilityDescription,
        },
        zoneAxis: opts.displayDashedIncompleteZones ? "x" : undefined,
        zones: opts.displayDashedIncompleteZones ? zones : undefined,
      });

      return results;
    },
  };
}
